/**
 * PrelaunchCleanupDialog — owner-only one-shot wipe of operational data.
 *
 * Replaces the old "Curățare date test" + "Migrare Var B" pair. The user must
 * literally type CURATARE in the confirmation field; the destructive button
 * stays disabled otherwise.
 *
 * UI contract:
 * - Destructive red colors (#9b1f2c) — clearly a danger zone
 * - Trash icon for visual semantics
 * - Two-step: warning panel → type confirmation → execute
 * - On success: show per-collection deletion counts and a "Done" button.
 */
import React, { useState } from "react";
import { AlertTriangle, Trash2, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const CONFIRM_WORD = "CURATARE";

export default function PrelaunchCleanupDialog({ onClose, onDone }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const canExecute = confirm.trim() === CONFIRM_WORD && !busy;

  const handleExecute = async () => {
    if (!canExecute) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/prelaunch-cleanup", { confirm: CONFIRM_WORD });
      setResult(r.data);
      toast.success(`Curățare finalizată: ${r.data.total} înregistrări șterse.`);
      onDone?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Curățarea a eșuat.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      data-testid="prelaunch-cleanup-dialog"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-start gap-3">
          <div className="shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#9b1f2c]/10 text-[#9b1f2c]">
            <AlertTriangle size={20} />
          </div>
          <div>
            <h2 className="font-display text-lg font-bold text-[#373A45]">Reset date prelaunch</h2>
            <p className="text-xs text-[#6B7280] mt-0.5">Acțiune ireversibilă. Doar înainte de lansare.</p>
          </div>
        </div>

        {/* Body */}
        {!result ? (
          <div className="px-6 py-5 space-y-4">
            <div className="rounded-xl bg-[#9b1f2c]/5 border border-[#9b1f2c]/20 px-4 py-3 text-sm text-[#5a1018]">
              <p className="font-semibold mb-1">Această acțiune va șterge:</p>
              <ul className="list-disc list-inside text-xs space-y-0.5 text-[#5a1018]">
                <li>TOATE leadurile și notele/timeline-ul lor</li>
                <li>TOATE înscrierile și evenimentele lor</li>
                <li>TOATE plățile (purchases) și istoricul lor</li>
                <li>TOATE grupurile de referral și recomandările</li>
                <li>Toate evenimentele de analytics interne</li>
                <li>Tokenii de magic-link în zbor (vor trebui regenerați)</li>
              </ul>
              <p className="font-semibold mt-3 mb-1">NU se șterge:</p>
              <ul className="list-disc list-inside text-xs space-y-0.5 text-[#373A45]">
                <li>Conturi admin / parteneri / utilizatori</li>
                <li>Cursuri și grupe configurate</li>
                <li>Directorul de școli</li>
                <li>Setări platformă (Netopia, prețuri, branding)</li>
                <li>Audit logs istorice</li>
              </ul>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#373A45] mb-1.5" htmlFor="cleanup-confirm">
                Pentru confirmare, tastează exact: <code className="bg-[#EDEEF0] px-1.5 py-0.5 rounded font-mono text-[#9b1f2c]">CURATARE</code>
              </label>
              <input
                id="cleanup-confirm"
                type="text"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-lg border border-[#E5E7EB] focus:border-[#9b1f2c] focus:ring-2 focus:ring-[#9b1f2c]/20 outline-none px-3 py-2 font-mono text-sm tracking-widest"
                placeholder="CURATARE"
                autoComplete="off"
                spellCheck={false}
                data-testid="prelaunch-cleanup-confirm-input"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-full text-sm font-medium text-[#373A45] border border-[#E5E7EB] hover:bg-[#F5F6F6]"
                data-testid="prelaunch-cleanup-cancel"
                disabled={busy}
              >
                Anulează
              </button>
              <button
                onClick={handleExecute}
                disabled={!canExecute}
                className={`px-4 py-2 rounded-full text-sm font-medium inline-flex items-center gap-1.5 ${
                  canExecute
                    ? "bg-[#9b1f2c] text-white hover:bg-[#7a1721]"
                    : "bg-[#9b1f2c]/30 text-white/80 cursor-not-allowed"
                }`}
                data-testid="prelaunch-cleanup-execute"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                {busy ? "Șterg..." : "Șterge definitiv"}
              </button>
            </div>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div className="rounded-xl bg-[#519872]/10 border border-[#519872]/30 px-4 py-3 text-sm text-[#2F6B4E] flex items-start gap-2">
              <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold">Curățare finalizată cu succes.</p>
                <p className="text-xs text-[#2F6B4E]/90 mt-0.5">
                  Total: <strong>{result.total}</strong> înregistrări șterse. Platforma este gata de lansare.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-[#E5E7EB] divide-y divide-[#E5E7EB]" data-testid="prelaunch-cleanup-report">
              {Object.entries(result.deleted || {}).map(([key, count]) => (
                <div key={key} className="px-4 py-2 flex items-center justify-between text-sm">
                  <span className="text-[#373A45] font-mono">{key}</span>
                  <span className="font-semibold text-[#9b1f2c]">{count}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-end pt-1">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-full text-sm font-medium bg-[#373A45] text-white hover:bg-[#1F2128]"
                data-testid="prelaunch-cleanup-close"
              >
                Închide
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
